'use client';

import { ArrowLeftIcon } from '@curology/ui-components-web/react/Icon/icons/ArrowLeftIcon';
import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';

import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import CcEntrySummary from '@/components/checkout/components/ccEntrySummary';
import { CcForm } from '@/components/checkout/components/ccForm';
import PriceSummary from '@/components/checkout/components/priceSummary';
import ShippingAddressForm from '@/components/checkout/components/shippingAddressForm';
import ShippingAddressPlaceholder from '@/components/checkout/components/shippingAddressPlaceholder';
import { CHECKOUT_STATES } from '@/components/checkout/consts/checkoutStates';
import { trackFieldPrePopulatedEvents } from '@/components/checkout/js/trackFieldPrePopulatedEvents';
import { activateSubscription } from '@/components/checkout/js/updateShippingAddressAndActivateTrial';
import { getDefaultCheckoutState } from '@/components/checkout/utils/checkout';
import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { useCheckout } from '@/components/flow/steps/checkout/hooks/useCheckout';
import { CHECKOUT_MODES } from '@/components/flow/steps/checkout/types';
import { useCheckoutMode } from '@/components/product-selection/hooks/useCheckoutMode';
import { usePurchaseContext } from '@/components/product-selection/hooks/usePurchaseContext';
import { useSubmittingForm } from '@/components/product-selection/hooks/useSubmittingForm';
import { useTotals } from '@/components/product-selection/hooks/useTotals';
import { AdditionalDisclaimer } from '@/components/productSelectionAndCheckoutAdditionalDisclaimer';
import { Button } from '@/components/squeeze';
import { DATA_KEYS } from '@/consts/dataKeys';
import { deleteProductEligibility } from '@/services/eligibilityService/actions';
import { type ShippingAddressFields } from '@/types/productSelectionAndCheckout/types';

import { useNavigation } from '../../hooks/useNavigation';

export const CheckoutPage = ({
  showFlashMessageOnCheckoutSuccess,
  navigateToProductSelectionMethod,
}: {
  showFlashMessageOnCheckoutSuccess?: boolean;
  /**
   * This controls how navigation back to product selection is handled.
   *   - searchParam: Navigates by updating a query param in the URL.
   *                  This is used for the old style of combined recommendations and checkout step.
   *   - flow: Navigates by using the useNavigation hook to go back to the previous step in the flow.
   */
  navigateToProductSelectionMethod?: 'searchParam' | 'flow';
}) => {
  const { brand } = useFlowContext();
  const {
    checkoutPaymentDetails,
    patient: initialPatientState,
    trialSizeItemPrices,
    oneTimeItemPrices,
    fullSizeItemPrices,
    flags,
  } = useDataProvider(DATA_KEYS.checkout);
  const { purchaseContext } = usePurchaseContext();

  const checkoutMode = useCheckoutMode();
  const { onSuccess } = useCheckout();
  const { treatmentCategory } = useFlowContext();
  const { onBack } = useNavigation();

  const purchaseContextAwareSmallPrices =
    purchaseContext === 'onetime' ? oneTimeItemPrices : trialSizeItemPrices;

  // Page state
  const [flashMessages, setFlashMessages] = useAtom(
    FlashMessagesState.flashMessagesAtom,
  );
  const [patient, setPatient] = useState(initialPatientState);
  const [shippingAddressFields, setShippingAddressFields] =
    useState<ShippingAddressFields>({
      apartmentSuite: '',
      city: '',
      firstName: patient.firstname || '',
      lastName: patient.lastname || '',
      state: patient.state || '',
      streetAddress: '',
      zipCode: patient.zipcode || '',
    });
  const { submittingForm, setSubmittingForm } = useSubmittingForm();
  const [trackedFieldPrePopulatedEvents, setTrackedFieldPrePopulatedEvents] =
    useState(false);

  // Payment state
  const [cardLastFour, setCardLastFour] = useState<string | undefined>(
    checkoutPaymentDetails.isCollectionRequired
      ? undefined
      : patient.cardLastFour ?? undefined,
  );
  const [checkoutState, setCheckoutState] = useState(
    getDefaultCheckoutState(
      checkoutPaymentDetails.isCollectionRequired,
      checkoutPaymentDetails.isPaymentRequired,
      patient,
    ),
  );

  useEffect(() => {
    if (!trackedFieldPrePopulatedEvents) {
      setTrackedFieldPrePopulatedEvents(true);

      trackFieldPrePopulatedEvents(shippingAddressFields);
    }
  }, [shippingAddressFields, trackedFieldPrePopulatedEvents]);

  const onConfirmPaymentMethod = async () => {
    setSubmittingForm(true);
    const subscriptionSuccessfullyActivated = await activateSubscription(
      brand,
      flashMessages,
      patient,
      setFlashMessages,
      treatmentCategory,
      checkoutMode === CHECKOUT_MODES.Trial,
      !!flags.agencySmallBottlesInSignUp,
    );
    if (subscriptionSuccessfullyActivated) {
      await deleteProductEligibility();
      onSuccess();
    } else {
      setSubmittingForm(false);
    }
  };

  const {
    futureShipmentTotal,
    trialTotalWithTax,
    trialTotalWithTaxAndWithoutDiscounts,
  } = useTotals();

  return (
    <div>
      <PriceSummary
        itemPrices={
          checkoutMode === CHECKOUT_MODES.Trial
            ? purchaseContextAwareSmallPrices
            : fullSizeItemPrices
        }
        trialTotalWithTaxAndWithoutDiscounts={
          trialTotalWithTaxAndWithoutDiscounts
        }
        trialTotalWithTax={trialTotalWithTax}
      />
      {(checkoutPaymentDetails.isCollectionRequired ||
        checkoutPaymentDetails.isPaymentRequired) && (
        <>
          <CcForm
            checkoutState={checkoutState}
            patient={patient}
            setCardLastFour={setCardLastFour}
            setCheckoutState={setCheckoutState}
            setPatient={setPatient}
            userMessage={checkoutPaymentDetails.userMessage}
          />
          <CcEntrySummary
            cardLastFour={cardLastFour}
            checkoutState={checkoutState}
            patient={patient}
            setCheckoutState={setCheckoutState}
          />
        </>
      )}
      <ShippingAddressPlaceholder
        patient={patient}
        checkoutState={checkoutState}
        setCheckoutState={setCheckoutState}
      />
      <ShippingAddressForm
        checkoutState={checkoutState}
        patient={patient}
        setPatient={setPatient}
        setShippingAddressFields={setShippingAddressFields}
        setSubmittingForm={setSubmittingForm}
        shippingAddressFields={shippingAddressFields}
        showFlashMessageOnCheckoutSuccess={showFlashMessageOnCheckoutSuccess}
        submittingForm={submittingForm}
        futureShipmentTotal={futureShipmentTotal}
        agencySmallBottlesInSignUp={!!flags.agencySmallBottlesInSignUp}
      />
      <AdditionalDisclaimer />
      {checkoutState === CHECKOUT_STATES.ccAndShippingAddressCompleted && (
        <div className="cur-mt-4">
          <Button
            className="cur-w-full"
            data-sentry-id="Confirm payment method and activate trial"
            onClick={onConfirmPaymentMethod}
            loading={submittingForm}
            disabled={submittingForm}
            size="lg"
          >
            Confirm payment method
          </Button>
        </div>
      )}
      <div className="cur-mt-4 cur-text-center">
        <Button
          buttonType="text"
          className="cur-no-underline"
          data-sentry-id="Back button"
          onClick={() => {
            if (navigateToProductSelectionMethod === 'searchParam') {
              window.location.assign('?checkout=false');
            } else {
              onBack();
            }
          }}
          iconStart={<ArrowLeftIcon />}
          state="dark"
        >
          Back
        </Button>
      </div>
    </div>
  );
};
